import { computeBatteryStacks, type BatteryStack } from "./battery-stack";
import { isShuntPassthrough } from "./cable-current";
import type { DefLookup } from "./catalog";
import { withoutDanglingCables } from "./diagnostics/cable-endpoints";
import { buildGraph, reachable, v, type Graph } from "./diagnostics/graph";
import type { System } from "./types";

export type ShuntBank = {
  bank: BatteryStack;
  batteryTerminalId: string;
  systemTerminalId: string;
  powerGraph: Graph;
};

const cache = new WeakMap<System, WeakMap<DefLookup, Map<string, ShuntBank | null>>>();

/** Identify the measured bank without crossing the shunt itself. Shared
 * negatives alone do not identify a bank; only the primary reference can
 * disambiguate them. AUX and converter transitions never join power rails. */
export function identifyShuntBank(system: System, lookup: DefLookup, nodeId: string): ShuntBank | null {
  let byLookup = cache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  let byNode = byLookup.get(lookup);
  if (!byNode) { byNode = new Map(); byLookup.set(lookup, byNode); }
  if (byNode.has(nodeId)) return byNode.get(nodeId)!;
  const result = identify(system, lookup, nodeId);
  byNode.set(nodeId, result);
  return result;
}

function identify(system: System, lookup: DefLookup, nodeId: string): ShuntBank | null {
  const node = system.nodes.find(n => n.id === nodeId);
  if (node?.kind !== "component") return null;
  const def = lookup(node.definitionId, node.customDefinition);
  if (!def || (!def.currentShunt && !isShuntPassthrough(def))) return null;
  const batteryTerminal = def.terminals.find(t => t.polarity === "negative" &&
    (def.currentShunt ? t.id === def.currentShunt.batteryTerminalId : t.direction === "input"));
  const systemTerminal = def.terminals.find(t => t.polarity === "negative" &&
    (def.currentShunt ? t.id === def.currentShunt.systemTerminalId : t.direction === "output"));
  if (!batteryTerminal || !systemTerminal || batteryTerminal.id === systemTerminal.id) return null;

  const validSystem = withoutDanglingCables(system, lookup);
  const powerGraph = buildGraph(validSystem, lookup, { excludeSense: true });
  const cut: Graph = {
    ...powerGraph,
    adj: new Map([...powerGraph.adj].map(([vertex, edges]) => [vertex, edges.filter(({ via }) =>
      !(via.kind === "intra" && via.link.nodeId === node.id &&
        ((via.link.from === batteryTerminal.id && via.link.to === systemTerminal.id) ||
         (via.link.from === systemTerminal.id && via.link.to === batteryTerminal.id))),
    )])),
  };
  const batterySide = reachable(cut, v(node.id, batteryTerminal.id));
  let candidates = computeBatteryStacks(validSystem, lookup).stacks.filter(bank =>
    bank.bottomNegatives.some(negative => batterySide.has(negative)));
  const reference = def.terminals.find(t => def.currentShunt?.referenceTerminalId
    ? t.id === def.currentShunt.referenceTerminalId : t.id === "vbat" || t.id === "batt-pos");
  if (reference) {
    const referenceSide = reachable(buildGraph(validSystem, lookup), v(node.id, reference.id));
    const identified = candidates.filter(bank => bank.topPositives.some(positive => referenceSide.has(positive)));
    if (identified.length > 0) candidates = identified;
  }
  if (candidates.length !== 1) return null;
  return { bank: candidates[0], batteryTerminalId: batteryTerminal.id, systemTerminalId: systemTerminal.id, powerGraph };
}
